import { supabaseClient } from '../supabase-client';

const BASE = import.meta.env.VITE_FILTER_API_URL;

export class FilterApiError extends Error {
  readonly code: string;
  readonly messageEn: string;
  readonly status: number;
  constructor(opts: { code: string; message: string; messageEn: string; status: number }) {
    super(opts.message);
    this.name = 'FilterApiError';
    this.code = opts.code;
    this.messageEn = opts.messageEn;
    this.status = opts.status;
  }
}

async function authHeaders(): Promise<HeadersInit> {
  const { data } = await supabaseClient.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error('not_authenticated');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

export async function filterApiFetch<T = unknown>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  if (!BASE) throw new Error('VITE_FILTER_API_URL not set');
  const headers = await authHeaders();
  const resp = await fetch(`${BASE}${path}`, {
    ...init,
    headers: { ...headers, ...(init.headers ?? {}) },
  });
  const body = (await resp.json().catch(() => null)) as {
    data?: T;
    error?: { code?: string; message?: string; message_en?: string };
  } | null;
  if (!resp.ok) {
    const code = body?.error?.code ?? 'UNKNOWN';
    const message = body?.error?.message ?? `HTTP ${resp.status}`;
    const messageEn = body?.error?.message_en ?? message;
    throw new FilterApiError({ code, message, messageEn, status: resp.status });
  }
  return (body?.data as T) ?? (body as unknown as T);
}

export interface FilterApiEnvelope<T> {
  data: T;
  pagination?: { total: number; page: number; page_size: number };
  request_id?: string;
}

// Like filterApiFetch but returns the full envelope (does NOT unwrap `data`).
// Used by the Refine data provider's getList to access pagination.total.
export async function filterApiFetchEnvelope<T = unknown>(
  path: string,
  init: RequestInit = {},
): Promise<FilterApiEnvelope<T>> {
  if (!BASE) throw new Error('VITE_FILTER_API_URL not set');
  const headers = await authHeaders();
  const resp = await fetch(`${BASE}${path}`, {
    ...init,
    headers: { ...headers, ...(init.headers ?? {}) },
  });
  const body = (await resp.json().catch(() => null)) as
    | (FilterApiEnvelope<T> & { error?: { code?: string; message?: string; message_en?: string } })
    | null;
  if (!resp.ok) {
    const code = body?.error?.code ?? 'UNKNOWN';
    const message = body?.error?.message ?? `HTTP ${resp.status}`;
    const messageEn = body?.error?.message_en ?? message;
    throw new FilterApiError({ code, message, messageEn, status: resp.status });
  }
  return (body ?? { data: undefined as unknown as T }) as FilterApiEnvelope<T>;
}
